import React, { useState } from 'react';
import {
    Pin, MoreHorizontal, Edit2, Trash2, Archive, ArchiveRestore, Lock, Users, Globe,
    Calendar, Briefcase, Hash, CheckSquare, Link2, Paperclip, MessageCircle,
    ThumbsUp, Heart, Smile, Copy, Check
} from 'lucide-react';
import ConfirmDeleteModal from './ConfirmDeleteModal';

const VISIBILITY_ICONS = {
    PRIVATE: { icon: Lock, label: 'Private', color: 'slate' },
    PROTECTED: { icon: Users, label: 'Team', color: 'sky' },
    PUBLIC: { icon: Globe, label: 'Public', color: 'emerald' }
};

const NoteCard = ({
    note,
    viewMode = 'list',
    onEdit,
    onDelete,
    onTogglePin,
    onToggleArchive,
    onTagClick,
    currentUserEmail,
    onNavigateToCase,
    cases = []
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const [copied, setCopied] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Extract tags from content
    const extractTags = (content) => {
        if (!content) return [];
        const tagPattern = /#[\w-]+/g;
        const matches = content.match(tagPattern) || [];
        return [...new Set(matches.map(t => t.substring(1)))];
    };

    // Count tasks in content
    const countTasks = (content) => {
        if (!content) return { total: 0, completed: 0 };
        const completedMatch = content.match(/- \[x\]/gi) || [];
        const incompleteMatch = content.match(/- \[ \]/gi) || [];
        return {
            total: completedMatch.length + incompleteMatch.length,
            completed: completedMatch.length
        };
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    // Get content preview (first few lines, strip markdown)
    const getPreview = (content, maxLength = 200) => {
        if (!content) return '';
        // Strip markdown syntax for preview
        let preview = content
            .replace(/#{1,6}\s/g, '')  // Headers
            .replace(/\*\*(.+?)\*\*/g, '$1')  // Bold
            .replace(/\*(.+?)\*/g, '$1')  // Italic
            .replace(/`([^`]+)`/g, '$1')  // Code
            .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')  // Links
            .replace(/- \[[ x]\]\s/gi, '')  // Task checkboxes
            .replace(/^- /gm, '')  // Bullet points
            .replace(/^\d+\.\s/gm, '');  // Numbered lists

        if (preview.length > maxLength) {
            preview = preview.substring(0, maxLength) + '...';
        }
        return preview;
    };

    // Copy note content to clipboard
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(note.content);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Copy shareable link to clipboard (only for Team/Public notes)
    const handleCopyLink = async (e) => {
        e?.stopPropagation();
        try {
            const baseUrl = window.location.origin;
            const noteLink = `${baseUrl}/notes?noteId=${note.id}`;
            await navigator.clipboard.writeText(noteLink);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    };

    // Check if note is shareable (Team or Public visibility)
    const isShareable = note.visibility === 'PROTECTED' || note.visibility === 'PUBLIC';

    const tags = extractTags(note.content);
    const tasks = countTasks(note.content);
    const visibility = VISIBILITY_ICONS[note.visibility] || VISIBILITY_ICONS.PRIVATE;
    const VisibilityIcon = visibility.icon;
    const hasAttachments = note.attachments && note.attachments.length > 0;
    const linkedCaseId = note.case_id || note.caseId;
    const createdAt = note.created_at || note.createdAt;
    const updatedAt = note.updated_at || note.updatedAt;

    // Look up the linked case name from cases array
    const linkedCase = linkedCaseId && cases.length > 0
        ? cases.find(c => String(c.id) === String(linkedCaseId))
        : null;
    const linkedCaseName = linkedCase?.name || linkedCase?.borrowerName || (linkedCaseId ? `Case #${linkedCaseId}` : null);

    // Delete confirmation modal
    const deleteModal = (
        <ConfirmDeleteModal
            isOpen={showDeleteConfirm}
            onConfirm={() => {
                setShowDeleteConfirm(false);
                onDelete?.();
            }}
            onCancel={() => setShowDeleteConfirm(false)}
            title="Delete Note"
            message="Are you sure you want to delete this note? This action cannot be undone."
        />
    );

    // Grid view card
    if (viewMode === 'grid') {
        return (
            <>
            {deleteModal}
            <div
                className={`group relative dark:bg-slate-900/50 bg-white border rounded-xl overflow-hidden transition-all hover:shadow-lg ${note.pinned
                    ? 'dark:border-amber-500/50 border-amber-300 shadow-amber-500/10'
                    : 'dark:border-slate-800 border-slate-200 hover:border-slate-300 dark:hover:border-slate-700'
                    }`}
            >
                {/* Pin indicator */}
                {note.pinned && (
                    <div className="absolute top-2 right-2 z-10">
                        <Pin size={14} className="text-amber-500 fill-amber-500" />
                    </div>
                )}

                {/* Content */}
                <div
                    className="p-4 cursor-pointer"
                    onClick={onEdit}
                >
                    <p className="dark:text-slate-300 text-slate-700 text-sm leading-relaxed line-clamp-4 mb-3">
                        {getPreview(note.content, 150)}
                    </p>

                    {/* Tags */}
                    {tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                            {tags.slice(0, 3).map(tag => (
                                <button
                                    key={tag}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onTagClick?.(tag);
                                    }}
                                    className="px-2 py-0.5 text-xs rounded-full dark:bg-amber-500/10 bg-amber-50 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 transition"
                                >
                                    #{tag}
                                </button>
                            ))}
                            {tags.length > 3 && (
                                <span className="px-2 py-0.5 text-xs text-slate-500">+{tags.length - 3}</span>
                            )}
                        </div>
                    )}

                    {/* Metadata row */}
                    <div className="flex items-center justify-between text-xs dark:text-slate-500 text-slate-400">
                        <div className="flex items-center gap-2">
                            <VisibilityIcon size={12} className={`text-${visibility.color}-500`} />
                            <span>{formatDate(createdAt)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {tasks.total > 0 && (
                                <span className="flex items-center gap-1">
                                    <CheckSquare size={12} />
                                    {tasks.completed}/{tasks.total}
                                </span>
                            )}
                            {hasAttachments && (
                                <Paperclip size={12} />
                            )}
                        </div>
                    </div>
                </div>

                {/* Hover actions */}
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t dark:from-slate-900 from-white to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center justify-end gap-1">
                        {isShareable && (
                            <button
                                onClick={handleCopyLink}
                                className={`p-1.5 rounded-lg transition ${linkCopied
                                    ? 'text-emerald-500 bg-emerald-500/10'
                                    : 'dark:text-slate-400 text-slate-500 hover:text-sky-500 hover:bg-sky-500/10'
                                }`}
                                title={linkCopied ? 'Link copied!' : 'Copy shareable link'}
                            >
                                {linkCopied ? <Check size={14} /> : <Link2 size={14} />}
                            </button>
                        )}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onTogglePin?.();
                            }}
                            className={`p-1.5 rounded-lg transition ${note.pinned
                                ? 'text-amber-500 hover:bg-amber-500/20'
                                : 'dark:text-slate-400 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'
                                }`}
                            title={note.pinned ? 'Unpin' : 'Pin'}
                        >
                            <Pin size={14} className={note.pinned ? 'fill-current' : ''} />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit?.();
                            }}
                            className="p-1.5 rounded-lg dark:text-slate-400 text-slate-500 hover:text-sky-500 hover:bg-sky-500/10 transition"
                            title="Edit"
                        >
                            <Edit2 size={14} />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleArchive?.();
                            }}
                            className="p-1.5 rounded-lg dark:text-slate-400 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                            title={note.archived ? 'Restore' : 'Archive'}
                        >
                            {note.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowDeleteConfirm(true);
                            }}
                            className="p-1.5 rounded-lg dark:text-slate-400 text-slate-500 hover:text-red-500 hover:bg-red-500/10 transition"
                            title="Delete"
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                </div>
            </div>
            </>
        );
    }

    // List view card (default)
    return (
        <>
        {deleteModal}
        <div
            className={`group relative dark:bg-slate-900/50 bg-white border rounded-xl p-4 transition-all hover:shadow-md ${note.pinned
                ? 'dark:border-amber-500/30 border-amber-200 bg-amber-50/50 dark:bg-amber-500/5'
                : 'dark:border-slate-800 border-slate-200 hover:border-slate-300 dark:hover:border-slate-700'
                }`}
        >
            <div className="flex items-start gap-4">
                {/* Pin indicator */}
                {note.pinned && (
                    <div className="flex-shrink-0 mt-1">
                        <Pin size={16} className="text-amber-500 fill-amber-500" />
                    </div>
                )}

                {/* Main content */}
                <div className="flex-1 min-w-0">
                    {/* Top row: preview + meta */}
                    <div
                        className="cursor-pointer"
                        onClick={onEdit}
                    >
                        <p className="dark:text-slate-200 text-slate-800 text-sm leading-relaxed mb-2">
                            {getPreview(note.content, viewMode === 'list' ? 300 : 150)}
                        </p>
                    </div>

                    {/* Tags */}
                    {tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                            {tags.map(tag => (
                                <button
                                    key={tag}
                                    onClick={() => onTagClick?.(tag)}
                                    className="px-2 py-0.5 text-xs rounded-full dark:bg-amber-500/10 bg-amber-50 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 transition"
                                >
                                    #{tag}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Bottom row: metadata + actions */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-xs dark:text-slate-500 text-slate-400">
                            {/* Visibility */}
                            <span className="flex items-center gap-1">
                                <VisibilityIcon size={12} className={`text-${visibility.color}-500`} />
                                {visibility.label}
                            </span>

                            {/* Date */}
                            <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {formatDate(createdAt)}
                                {updatedAt && updatedAt !== createdAt && (
                                    <span className="text-slate-500"> (edited)</span>
                                )}
                            </span>

                            {/* Tasks */}
                            {tasks.total > 0 && (
                                <span className="flex items-center gap-1">
                                    <CheckSquare size={12} className={tasks.completed === tasks.total ? 'text-emerald-500' : ''} />
                                    {tasks.completed}/{tasks.total} tasks
                                </span>
                            )}

                            {/* Attachments */}
                            {hasAttachments && (
                                <span className="flex items-center gap-1">
                                    <Paperclip size={12} />
                                    {note.attachments.length}
                                </span>
                            )}

                            {/* Linked case */}
                            {linkedCaseId && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onNavigateToCase?.(linkedCaseId);
                                    }}
                                    className="flex items-center gap-1 text-sky-500 hover:underline cursor-pointer max-w-[150px]"
                                    title={linkedCaseName}
                                >
                                    <Briefcase size={12} className="flex-shrink-0" />
                                    <span className="truncate">{linkedCaseName}</span>
                                </button>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {isShareable && (
                                <button
                                    onClick={handleCopyLink}
                                    className={`p-1.5 rounded-lg transition ${linkCopied
                                        ? 'text-emerald-500 bg-emerald-500/10'
                                        : 'dark:text-slate-400 text-slate-500 hover:text-sky-500 hover:bg-sky-500/10'
                                    }`}
                                    title={linkCopied ? 'Link copied!' : 'Copy shareable link'}
                                >
                                    {linkCopied ? <Check size={14} /> : <Link2 size={14} />}
                                </button>
                            )}
                            <button
                                onClick={handleCopy}
                                className="p-1.5 rounded-lg dark:text-slate-400 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                                title="Copy content"
                            >
                                {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                            </button>
                            <button
                                onClick={onTogglePin}
                                className={`p-1.5 rounded-lg transition ${note.pinned
                                    ? 'text-amber-500 hover:bg-amber-500/20'
                                    : 'dark:text-slate-400 text-slate-500 hover:text-amber-500 hover:bg-amber-500/10'
                                    }`}
                                title={note.pinned ? 'Unpin' : 'Pin'}
                            >
                                <Pin size={14} className={note.pinned ? 'fill-current' : ''} />
                            </button>
                            <button
                                onClick={onEdit}
                                className="p-1.5 rounded-lg dark:text-slate-400 text-slate-500 hover:text-sky-500 hover:bg-sky-500/10 transition"
                                title="Edit"
                            >
                                <Edit2 size={14} />
                            </button>
                            <button
                                onClick={onToggleArchive}
                                className="p-1.5 rounded-lg dark:text-slate-400 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                                title={note.archived ? 'Restore' : 'Archive'}
                            >
                                {note.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                            </button>

                            {/* More menu */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowMenu(!showMenu)}
                                    className="p-1.5 rounded-lg dark:text-slate-400 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                                >
                                    <MoreHorizontal size={14} />
                                </button>
                                {showMenu && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-10"
                                            onClick={() => setShowMenu(false)}
                                        />
                                        <div className="absolute right-0 bottom-full mb-1 w-36 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-lg shadow-xl z-20 overflow-hidden">
                                            <button
                                                onClick={() => {
                                                    setShowMenu(false);
                                                    setShowDeleteConfirm(true);
                                                }}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-500/10 transition"
                                            >
                                                <Trash2 size={14} />
                                                Delete
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default NoteCard;
